import { Injectable } from '@angular/core';
import { catchError, forkJoin, of, tap, timeout, finalize } from 'rxjs';
import { ApiService } from './api.service';
import { CommonService } from './common.service';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root',
})
export class PreloadService {
  private static readonly STORAGE_KEYS = {
    appliances: 'em_appliances_cache',
    cities: 'em_cities_cache',
    featured: 'em_featured_cache',
  };

  constructor(
    private api: ApiService,
    private shared: CommonService,
    private loadingService: LoadingService
  ) {}

  loadInitialData(): Promise<void> {
    // 1. Immediately hydrate from cache if available so UI renders with 0ms delay
    const hasCachedData = this.hydrateFromCache();

    if (hasCachedData) {
      this.shared.notifyReferenceDataUpdated();
    } else {
      // First-time visit: show branded loader while essential reference data arrives
      this.loadingService.show('Loading ElectraMaster...');
    }

    // Release the startup overlay promptly without cancelling slow reference reads.
    let startupLoaderOpen = !hasCachedData;
    const closeStartupLoader = () => {
      if (startupLoaderOpen) {
        startupLoaderOpen = false;
        this.loadingService.hide();
      }
    };
    const loaderTimer = startupLoaderOpen ? setTimeout(closeStartupLoader, 2500) : undefined;
    const appliances$ = this.api.get(ApiService.appliance_GetAll_Appliance).pipe(
      timeout(15000),
      tap(response => {
        if (response?.data?.appliances) {
          this.shared.appliances = response.data.appliances;
          this.saveToCache(PreloadService.STORAGE_KEYS.appliances, this.shared.appliances);
          this.shared.notifyReferenceDataUpdated();
        }
      }),
      catchError((error) => of({ error }))
    );
    const cities$ = this.api.get(ApiService.Business_Get_BusinessCity_Pincode).pipe(
      timeout(15000),
      tap(response => {
        if (response?.data) {
          this.shared.businessCity = response.data;
          this.saveToCache(PreloadService.STORAGE_KEYS.cities, this.shared.businessCity);
          this.shared.notifyReferenceDataUpdated();
        }
      }),
      catchError((error) => of({ error }))
    );
    const featured$ = this.api.get(ApiService.user_GetAllFeaturedBusiness).pipe(
      timeout(15000),
      tap(response => {
        if (Array.isArray(response?.data)) {
          this.shared.featuredBusinesses = response.data;
          this.saveToCache(PreloadService.STORAGE_KEYS.featured, this.shared.featuredBusinesses);
          this.shared.notifyReferenceDataUpdated();
        }
      }),
      catchError((error) => of({ error }))
    );

    // Publish each result as it arrives; one slow endpoint must not hide the others.
    const request$ = forkJoin({
      appliances: appliances$,
      cities: cities$,
      featured: featured$,
    }).pipe(
      catchError(() => of(null)),
      finalize(() => {
        clearTimeout(loaderTimer);
        closeStartupLoader();
      })
    );

    // Fire request in background without blocking Angular application bootstrap
    request$.subscribe();
    return Promise.resolve();
  }

  private hydrateFromCache(): boolean {
    try {
      const rawAppliances = localStorage.getItem(PreloadService.STORAGE_KEYS.appliances);
      const rawCities = localStorage.getItem(PreloadService.STORAGE_KEYS.cities);
      const rawFeatured = localStorage.getItem(PreloadService.STORAGE_KEYS.featured);

      let loaded = false;
      if (rawAppliances) {
        this.shared.appliances = JSON.parse(rawAppliances);
        loaded = true;
      }
      if (rawCities) {
        this.shared.businessCity = JSON.parse(rawCities);
        loaded = true;
      }
      if (rawFeatured) {
        this.shared.featuredBusinesses = JSON.parse(rawFeatured);
        loaded = true;
      }
      return loaded;
    } catch {
      return false;
    }
  }

  private saveToCache(key: string, data: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // Storage quota or private browsing mode
    }
  }
}
